import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  getDetailsOfRegisteredUserWithPaymentDetails,
  setSearchTerm,
  fetchSalesRepsUnderCSE,
  setSelectedCSE,
  setSalesRepOptions,
  setSelectedSalesRep,
} from "../../store/slices/epcSlice";
import { Search, Filter, ChevronDown } from "lucide-react";
import SearchInput from "../Search/SearchInput";
import Button, { ButtonType } from "../Assets/Button";
import { usePreventWheelChange } from "../../utils/usePreventWheelChange";
import PaginationControls from "../pagination/PaginationControls";
import { getUser } from "../../utils/auth";

type ActiveFilter = "dueAmount" | "salesTeam";

const PaymentsTable: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();
  const [minDueInput, setMinDueInput] = useState("");
  const [maxDueInput, setMaxDueInput] = useState("");

  const [minDue, setMinDue] = useState<number | null>(null);
  const [maxDue, setMaxDue] = useState<number | null>(null);

  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [isPrevLoading, setIsPrevLoading] = useState(false);

  const currentUser = getUser();

  const handleWheelPrevent = usePreventWheelChange();

  const {
    registeredUserInfoWithDetails,
    loadingState,
    searchTerm,
    selectedSalesRep,
    selectedCseIdForCustomerDetails,
    cseWithSalesReps,
    selectedCSE,
    salesRepOptions,
    totalPagesOfRegisteredUsers,
  } = useSelector((state: RootState) => state.epc);

  const fetchCustomers = (page: number = 1) => {
    return dispatch(
      getDetailsOfRegisteredUserWithPaymentDetails({
        salesRepId: selectedSalesRep,
        cseID: selectedCseIdForCustomerDetails,
        search: searchTerm,
        page,
      }),
    );
  };

  useEffect(() => {
    fetchCustomers(1);
    setCurrentPage(1);
  }, [selectedSalesRep, selectedCseIdForCustomerDetails, searchTerm]);

  useEffect(() => {
    dispatch(fetchSalesRepsUnderCSE());
  }, [dispatch]);

  const handleSearch = (term: string) => {
    dispatch(setSearchTerm(term));
    setCurrentPage(1);
  };

  const handleNextPage = async () => {
    if (currentPage >= totalPagesOfRegisteredUsers) return;
    setIsNextLoading(true);
    try {
      const nextPage = currentPage + 1;
      await fetchCustomers(nextPage);
      setCurrentPage(nextPage);
    } finally {
      setIsNextLoading(false);
    }
  };

  const handlePreviousPage = async () => {
    if (currentPage <= 1) return;
    setIsPrevLoading(true);
    try {
      const prevPage = currentPage - 1;
      await fetchCustomers(prevPage);
      setCurrentPage(prevPage);
    } finally {
      setIsPrevLoading(false);
    }
  };

  const handleCSEChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const cseId = e.target.value;

    dispatch(setSelectedCSE(cseId));

    const selected = cseWithSalesReps.find((item) => item.cse._id === cseId);

    dispatch(setSalesRepOptions(selected?.salesReps || []));
    setCurrentPage(1);
  };

  const handleSalesRepChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setSelectedSalesRep(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="p-6 bg-white">
      <div>
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
          {/* 🔍 Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

            <SearchInput
              placeholder="Search customers..."
              onSearch={handleSearch}
              className="w-full sm:w-80 md:w-96 pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-md
        focus:ring-1 focus:ring-navyBlue-primary focus:border-navyBlue-primary
        outline-none transition-all text-sm text-gray-700
        placeholder:text-gray-400 shadow-sm"
            />
          </div>

          <div className="relative w-full sm:w-auto inline-flex items-center gap-2 border rounded-md px-3 py-2 bg-white">
            <Filter className="h-4 w-4 text-gray-500 flex-shrink-0" />
            <select
              onChange={(e) => {
                const filter = e.target.value as ActiveFilter;

                if (filter && !activeFilters.includes(filter)) {
                  setActiveFilters((prev) => [...prev, filter]);
                }

                e.target.value = "";
              }}
              className="absolute text-sm inset-0 w-full h-full opacity-0 cursor-pointer"
            >
              <option value="">Add Filter</option>

              {!activeFilters.includes("dueAmount") && (
                <option value="dueAmount">Due Amount</option>
              )}

              {!activeFilters.includes("salesTeam") && !currentUser?.role && (
                <option value="salesTeam">Sales Team</option>
              )}
            </select>
            <span className="flex-1 ml-2 text-sm text-gray-600 pointer-events-none">
              Add Filter
            </span>
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className="mb-4 bg-gray-50 border rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-gray-700">
                Filters
              </span>

              <Button
                type={ButtonType.Cancel}
                onClick={() => {
                  setActiveFilters([]);
                  setMinDue(null);
                  setMaxDue(null);
                  setMinDueInput("");
                  setMaxDueInput("");
                  dispatch(setSelectedCSE(""));
                  dispatch(setSelectedSalesRep(""));
                }}
                className="text-xs bg-white text-red-500"
              >
                Clear all
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Due Amount */}
              {activeFilters.includes("dueAmount") && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600">
                    Due Amount
                  </label>

                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="number"
                      placeholder="Min ₹"
                      value={minDueInput}
                      onWheel={handleWheelPrevent}
                      onChange={(e) => setMinDueInput(e.target.value)}
                      className="w-full sm:w-24 px-3 py-2 border rounded-md text-sm"
                    />

                    <span className="text-gray-400">–</span>

                    <input
                      type="number"
                      placeholder="Max ₹"
                      value={maxDueInput}
                      onWheel={handleWheelPrevent}
                      onChange={(e) => setMaxDueInput(e.target.value)}
                      className="w-24 px-3 py-2 border rounded-md text-sm"
                    />

                    <Button
                      type={ButtonType.Submit}
                      onClick={() => {
                        setMinDue(minDueInput ? Number(minDueInput) : null);
                        setMaxDue(maxDueInput ? Number(maxDueInput) : null);
                      }}
                      className="text-xs"
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              )}
              {/* CSE */}
              {activeFilters.includes("salesTeam") && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600">
                    CSE
                  </label>

                  <select
                    value={selectedCSE || ""}
                    onChange={handleCSEChange}
                    className="px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="">All CSE</option>

                    {cseWithSalesReps.map((item) => (
                      <option key={item.cse._id} value={item.cse._id}>
                        {item.cse.username}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {/* Sales Rep */}
              {activeFilters.includes("salesTeam") && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600">
                    Sales Representative
                  </label>

                  <select
                    value={selectedSalesRep || ""}
                    onChange={handleSalesRepChange}
                    className="px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="">All Sales Reps</option>

                    {salesRepOptions.map((rep) => (
                      <option key={rep._id} value={rep._id}>
                        {rep.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="w-full overflow-x-scroll border border-gray-200 rounded-xl shadow-sm">
          <table className="min-w-[900px] w-full text-sm text-gray-700">
            <thead className="bg-gray-100 border-b sticky top-0 z-10">
              <tr>
                <th className="px-5 py-3 text-left font-semibold uppercase text-xs tracking-wide whitespace-nowrap">
                  <div className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-2 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Customer
                  </div>
                </th>

                <th className="px-5 py-3 text-left font-semibold uppercase text-xs tracking-wide whitespace-nowrap">
                  <div className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-2 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                    Sales Representative
                  </div>
                </th>
                <th className="px-5 py-3 text-center font-semibold uppercase text-xs tracking-wide whitespace-nowrap">
                  <div className="flex items-center justify-center gap-2">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4a8 8 0 018 8v4H4v-4a8 8 0 018-8z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 12l3-3"
                      />
                    </svg>
                    Plant Size
                  </div>
                </th>

                <th className="px-5 py-3 text-right font-semibold uppercase text-xs tracking-wide whitespace-nowrap">
                  <div className="flex items-center justify-end gap-1.5">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V6m0 12v-2"
                      />
                    </svg>
                    Total Bill
                  </div>
                </th>

                <th className="px-5 py-3 text-right font-semibold uppercase text-xs tracking-wide whitespace-nowrap">
                  <div className="flex items-center justify-end gap-1.5">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
                      />
                    </svg>
                    Paid
                  </div>
                </th>
                <th className="px-5 py-3 text-right font-semibold uppercase text-xs tracking-wide whitespace-nowrap">
                  <div className="flex items-center justify-end gap-1.5">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.5 3.5a2.1 2.1 0 013 3L8 18l-4 1 1-4 11.5-11.5z"
                      />
                    </svg>
                    Adjustment
                  </div>
                </th>
                <th className="px-5 py-3 text-right font-semibold uppercase text-xs tracking-wide whitespace-nowrap">
                  <div className="flex items-center justify-end gap-1.5">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="12" cy="12" r="9" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 8l-2 2 2 2"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 16l2-2-2-2"
                      />
                    </svg>
                    Round Off
                  </div>
                </th>

                <th className="px-5 py-3 text-right font-semibold uppercase text-xs tracking-wide whitespace-nowrap">
                  <div className="flex items-center justify-end gap-1.5">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
                      />
                    </svg>
                    Due
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              {/* Loading */}
              {loadingState.fetchingPaymentsLoading && (
                <tr>
                  <td colSpan={5} className="text-center py-10 text-gray-500">
                    Loading payment details...
                  </td>
                </tr>
              )}

              {!loadingState.fetchingPaymentsLoading &&
                registeredUserInfoWithDetails.map(
                  (user: any, index: number) => {
                    const payment = user.currentPayment;
                    const confirmedQuote = user.quote?.isConfirmed
                      ? user.quote
                      : null;

                    const total =
                      payment?.totalBillAmount ??
                      confirmedQuote?.totalCost ??
                      0;
                    const plantSize = user.quote?.plantSize;

                    const paid = payment?.paymentApproved ?? 0;
                    const roundOff = payment?.roundOffAmount ?? 0;
                    const adjustmentAmount =
                      payment?.quote?.adjustmentAmount ?? 0;
                    const remaining =
                      total - paid - roundOff - adjustmentAmount;

                    const min = minDue ? Number(minDue) : null;
                    const max = maxDue ? Number(maxDue) : null;

                    if (min !== null && remaining < min) return null;
                    if (max !== null && remaining > max) return null;

                    return (
                      <tr
                        key={user._id}
                        className={`border-b transition-colors ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        } hover:bg-indigo-50`}
                      >
                        <td className="px-5 py-4 font-medium text-gray-900">
                          {user.customerName}
                        </td>

                        <td className="px-5 py-4 text-gray-600">
                          {user.salesRepresentative?.name ||
                            user.cseID.username}
                        </td>
                        <td className="px-5 py-4 text-center font-medium text-gray-900">
                          {plantSize} KW
                        </td>

                        <td className="px-5 py-4 text-right font-medium">
                          ₹{total.toLocaleString()}
                        </td>

                        <td className="px-5 py-4 text-right font-medium text-green-600">
                          ₹{paid.toLocaleString()}
                        </td>
                        <td className="px-5 py-4 text-right font-medium text-gray-900">
                          ₹{adjustmentAmount.toLocaleString()}
                        </td>
                        <td className="px-5 py-4 text-right font-medium text-gray-900">
                          ₹{roundOff.toLocaleString()}
                        </td>

                        <td className="px-5 py-4 text-right font-medium">
                          <span
                            className={`${
                              remaining > 0 ? "text-red-600" : "text-green-600"
                            }`}
                          >
                            ₹{remaining.toLocaleString()}
                          </span>
                        </td>
                      </tr>
                    );
                  },
                )}

              {!loadingState.fetchingPaymentsLoading &&
                registeredUserInfoWithDetails.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-gray-500">
                      No payment records available
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>
        {!loadingState.fetchingPaymentsLoading &&
          totalPagesOfRegisteredUsers > 1 && (
            <div className="mt-4">
              <PaginationControls
                isNextFetching={isNextLoading}
                isPrevFetching={isPrevLoading}
                currentPage={currentPage}
                totalPages={totalPagesOfRegisteredUsers}
                onNext={handleNextPage}
                onPrev={handlePreviousPage}
              />
            </div>
          )}
      </div>
    </div>
  );
};

export default PaymentsTable;
